import React, { useEffect, useState } from 'react';
import { getErrorMessage } from '@/lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Mail, 
  Bell, 
  Smartphone,
  Settings2,
  FileText,
  Send,
  Key,
  Check,
  AlertCircle,
  Save,
  RefreshCw,
  Server,
  Cloud
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminNotificationsPage = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [testingEmail, setTestingEmail] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/notification-settings`);
      setSettings(res.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Errore nel caricamento delle impostazioni');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/admin/notification-settings`, settings);
      toast.success('Impostazioni salvate con successo');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const generateVapidKeys = async () => {
    try {
      const res = await axios.post(`${API}/admin/notification-settings/generate-vapid`);
      setSettings(prev => ({
        ...prev,
        web_push_config: {
          ...prev.web_push_config,
          vapid_public_key: res.data.vapid_public_key,
          vapid_private_key: res.data.vapid_private_key
        }
      }));
      toast.success('Chiavi VAPID generate');
    } catch (error) {
      toast.error('Errore nella generazione delle chiavi');
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Inserisci un indirizzo email');
      return;
    }
    try {
      setTestingEmail(true);
      await axios.post(`${API}/admin/notification-settings/test-email?to_email=${encodeURIComponent(testEmail)}`);
      toast.success(`Email di test inviata a ${testEmail}`);
    } catch (error) {
      toast.error(getErrorMessage(error, "Errore nell'invio"));
    } finally {
      setTestingEmail(false);
    }
  };

  const updateSetting = (path, value) => {
    setSettings(prev => {
      const newSettings = { ...prev };
      const keys = path.split('.');
      let current = newSettings;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newSettings;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-notifications-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold">Configurazione Notifiche</h1>
          <p className="text-muted-foreground mt-1">
            Gestisci i provider email, push e le regole di notifica
          </p>
        </div>
        <Button onClick={saveSettings} disabled={saving} data-testid="save-settings-btn">
          {saving ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Salva Impostazioni
        </Button>
      </div>

      <Tabs defaultValue="email" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="email" className="gap-2">
            <Mail className="h-4 w-4" />
            Email
          </TabsTrigger>
          <TabsTrigger value="webpush" className="gap-2">
            <Bell className="h-4 w-4" />
            Web Push
          </TabsTrigger>
          <TabsTrigger value="firebase" className="gap-2">
            <Smartphone className="h-4 w-4" />
            Firebase
          </TabsTrigger>
          <TabsTrigger value="events" className="gap-2">
            <Settings2 className="h-4 w-4" />
            Eventi
          </TabsTrigger>
          <TabsTrigger value="template" className="gap-2">
            <FileText className="h-4 w-4" />
            Template
          </TabsTrigger>
        </TabsList>

        {/* TAB EMAIL */}
        <TabsContent value="email" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Provider Email
              </CardTitle>
              <CardDescription>
                Configura il servizio per l'invio delle email transazionali
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Provider Attivo</Label>
                <Select 
                  value={settings?.email_provider || 'none'} 
                  onValueChange={(v) => updateSetting('email_provider', v)}
                >
                  <SelectTrigger data-testid="email-provider-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <span className="flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 text-muted-foreground" />
                        Nessuno (disabilitato)
                      </span>
                    </SelectItem>
                    <SelectItem value="smtp">
                      <span className="flex items-center gap-2">
                        <Server className="h-4 w-4" />
                        SMTP / IMAP Diretto
                      </span>
                    </SelectItem>
                    <SelectItem value="sendgrid">
                      <span className="flex items-center gap-2">
                        <Cloud className="h-4 w-4" />
                        SendGrid
                      </span>
                    </SelectItem>
                    <SelectItem value="magnews">
                      <span className="flex items-center gap-2">
                        <Cloud className="h-4 w-4" />
                        Magnews
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* SMTP CONFIG */}
              {settings?.email_provider === 'smtp' && (
                <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                  <h4 className="font-medium flex items-center gap-2">
                    <Server className="h-4 w-4" />
                    Configurazione SMTP / IMAP
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Host Server</Label>
                      <Input 
                        placeholder="smtp.example.com" 
                        value={settings?.smtp_config?.host || ''}
                        onChange={(e) => updateSetting('smtp_config.host', e.target.value)}
                        data-testid="smtp-host"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Porta</Label>
                      <Input 
                        type="number"
                        placeholder="587" 
                        value={settings?.smtp_config?.port || 587}
                        onChange={(e) => updateSetting('smtp_config.port', parseInt(e.target.value))}
                        data-testid="smtp-port"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Username</Label>
                      <Input 
                        placeholder="user@example.com" 
                        value={settings?.smtp_config?.username || ''}
                        onChange={(e) => updateSetting('smtp_config.username', e.target.value)}
                        data-testid="smtp-username"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Password</Label>
                      <Input 
                        type="password"
                        placeholder="••••••••" 
                        value={settings?.smtp_config?.password || ''}
                        onChange={(e) => updateSetting('smtp_config.password', e.target.value)}
                        data-testid="smtp-password"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email Mittente</Label>
                      <Input 
                        placeholder="noreply@example.com" 
                        value={settings?.smtp_config?.from_email || ''}
                        onChange={(e) => updateSetting('smtp_config.from_email', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Nome Mittente</Label>
                      <Input 
                        placeholder="HORECA Platform" 
                        value={settings?.smtp_config?.from_name || ''}
                        onChange={(e) => updateSetting('smtp_config.from_name', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-6 pt-2">
                    <div className="flex items-center gap-2">
                      <Switch 
                        checked={settings?.smtp_config?.use_tls || false}
                        onCheckedChange={(v) => updateSetting('smtp_config.use_tls', v)}
                      />
                      <Label>Usa TLS</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch 
                        checked={settings?.smtp_config?.use_ssl || false}
                        onCheckedChange={(v) => updateSetting('smtp_config.use_ssl', v)}
                      />
                      <Label>Usa SSL</Label>
                    </div>
                  </div>
                </div>
              )}

              {/* SENDGRID CONFIG */}
              {settings?.email_provider === 'sendgrid' && (
                <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                  <h4 className="font-medium flex items-center gap-2">
                    <Cloud className="h-4 w-4" />
                    Configurazione SendGrid
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 space-y-2">
                      <Label>API Key</Label>
                      <Input 
                        type="password"
                        placeholder="SG.xxxxxx" 
                        value={settings?.sendgrid_config?.api_key || ''}
                        onChange={(e) => updateSetting('sendgrid_config.api_key', e.target.value)}
                        data-testid="sendgrid-api-key"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email Mittente</Label>
                      <Input 
                        placeholder="noreply@example.com" 
                        value={settings?.sendgrid_config?.from_email || ''}
                        onChange={(e) => updateSetting('sendgrid_config.from_email', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Nome Mittente</Label>
                      <Input 
                        placeholder="HORECA Platform" 
                        value={settings?.sendgrid_config?.from_name || ''}
                        onChange={(e) => updateSetting('sendgrid_config.from_name', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* MAGNEWS CONFIG */}
              {settings?.email_provider === 'magnews' && (
                <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                  <h4 className="font-medium flex items-center gap-2">
                    <Cloud className="h-4 w-4" />
                    Configurazione Magnews
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>API Key</Label>
                      <Input 
                        type="password"
                        placeholder="API Key" 
                        value={settings?.magnews_config?.api_key || ''}
                        onChange={(e) => updateSetting('magnews_config.api_key', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>API Secret</Label>
                      <Input 
                        type="password"
                        placeholder="API Secret" 
                        value={settings?.magnews_config?.api_secret || ''}
                        onChange={(e) => updateSetting('magnews_config.api_secret', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email Mittente</Label>
                      <Input 
                        placeholder="noreply@example.com" 
                        value={settings?.magnews_config?.from_email || ''}
                        onChange={(e) => updateSetting('magnews_config.from_email', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Nome Mittente</Label>
                      <Input 
                        placeholder="HORECA Platform" 
                        value={settings?.magnews_config?.from_name || ''}
                        onChange={(e) => updateSetting('magnews_config.from_name', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* TEST EMAIL */}
              {settings?.email_provider !== 'none' && (
                <div className="flex items-end gap-4 pt-4 border-t">
                  <div className="flex-1 space-y-2">
                    <Label>Test Configurazione</Label>
                    <Input 
                      placeholder="test@example.com" 
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      data-testid="test-email-input"
                    />
                  </div>
                  <Button 
                    onClick={sendTestEmail} 
                    disabled={testingEmail}
                    variant="outline"
                    data-testid="send-test-email-btn"
                  >
                    {testingEmail ? (
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="mr-2 h-4 w-4" />
                    )}
                    Invia Email di Test
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB WEB PUSH */}
        <TabsContent value="webpush" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Web Push Notifications
              </CardTitle>
              <CardDescription>
                Notifiche push native del browser (gratuite, senza servizi esterni)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium">Abilita Web Push</p>
                  <p className="text-sm text-muted-foreground">
                    Gli utenti potranno ricevere notifiche anche quando il browser è chiuso
                  </p>
                </div>
                <Switch 
                  checked={settings?.web_push_config?.enabled || false}
                  onCheckedChange={(v) => updateSetting('web_push_config.enabled', v)}
                  data-testid="webpush-enabled"
                />
              </div>

              {settings?.web_push_config?.enabled && (
                <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium flex items-center gap-2">
                      <Key className="h-4 w-4" />
                      Chiavi VAPID
                    </h4>
                    <Button variant="outline" size="sm" onClick={generateVapidKeys}>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Genera Nuove Chiavi
                    </Button>
                  </div>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Chiave Pubblica</Label>
                      <Input 
                        value={settings?.web_push_config?.vapid_public_key || ''}
                        onChange={(e) => updateSetting('web_push_config.vapid_public_key', e.target.value)}
                        placeholder="Chiave pubblica VAPID"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Chiave Privata</Label>
                      <Input 
                        type="password"
                        value={settings?.web_push_config?.vapid_private_key || ''}
                        onChange={(e) => updateSetting('web_push_config.vapid_private_key', e.target.value)}
                        placeholder="Chiave privata VAPID"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email di Contatto</Label>
                      <Input 
                        value={settings?.web_push_config?.vapid_email || ''}
                        onChange={(e) => updateSetting('web_push_config.vapid_email', e.target.value)}
                        placeholder="admin@example.com"
                      />
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB FIREBASE */}
        <TabsContent value="firebase" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Smartphone className="h-5 w-5" />
                Firebase Cloud Messaging
              </CardTitle>
              <CardDescription>
                Notifiche push avanzate tramite Firebase (opzionale)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium">Abilita Firebase</p>
                  <p className="text-sm text-muted-foreground">
                    Richiede un progetto Firebase configurato
                  </p>
                </div>
                <Switch 
                  checked={settings?.firebase_config?.enabled || false}
                  onCheckedChange={(v) => updateSetting('firebase_config.enabled', v)}
                  data-testid="firebase-enabled"
                />
              </div>

              {settings?.firebase_config?.enabled && (
                <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Project ID</Label>
                      <Input 
                        value={settings?.firebase_config?.project_id || ''}
                        onChange={(e) => updateSetting('firebase_config.project_id', e.target.value)}
                        placeholder="my-project-id"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Server Key</Label>
                      <Input 
                        type="password"
                        value={settings?.firebase_config?.server_key || ''}
                        onChange={(e) => updateSetting('firebase_config.server_key', e.target.value)}
                        placeholder="Server Key da Firebase Console"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Config JSON (opzionale)</Label>
                    <Textarea 
                      value={settings?.firebase_config?.config_json || ''}
                      onChange={(e) => updateSetting('firebase_config.config_json', e.target.value)}
                      placeholder='{"apiKey": "...", "projectId": "..."}'
                      rows={4}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB EVENTI */}
        <TabsContent value="events" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings2 className="h-5 w-5" />
                Eventi e Trigger
              </CardTitle>
              <CardDescription>
                Configura quali eventi generano notifiche e su quali canali
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { key: 'new_order', label: 'Nuovo Ordine Ricevuto', desc: 'Quando un seller riceve un nuovo ordine' },
                  { key: 'order_confirmed', label: 'Ordine Confermato', desc: 'Quando un ordine viene confermato dal seller' },
                  { key: 'order_status_changed', label: 'Cambio Stato Ordine', desc: 'Quando lo stato di un ordine cambia' },
                  { key: 'new_message', label: 'Nuovo Messaggio', desc: 'Quando arriva un nuovo messaggio in chat' },
                  { key: 'price_change', label: 'Variazione Prezzo', desc: 'Quando un prezzo nel listino viene modificato' },
                  { key: 'low_stock', label: 'Stock Basso', desc: 'Quando un prodotto scende sotto la soglia minima' },
                  { key: 'new_relationship_request', label: 'Nuova Richiesta Relazione', desc: 'Quando un buyer richiede accesso al catalogo' },
                  { key: 'relationship_approved', label: 'Relazione Approvata', desc: 'Quando una richiesta viene approvata' },
                ].map(event => (
                  <div key={event.key} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium">{event.label}</p>
                      <p className="text-sm text-muted-foreground">{event.desc}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Switch 
                          checked={settings?.events?.[event.key]?.in_app_enabled ?? true}
                          onCheckedChange={(v) => updateSetting(`events.${event.key}.in_app_enabled`, v)}
                        />
                        <span className="text-xs text-muted-foreground">In-App</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch 
                          checked={settings?.events?.[event.key]?.email_enabled ?? true}
                          onCheckedChange={(v) => updateSetting(`events.${event.key}.email_enabled`, v)}
                        />
                        <span className="text-xs text-muted-foreground">Email</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch 
                          checked={settings?.events?.[event.key]?.push_enabled ?? true}
                          onCheckedChange={(v) => updateSetting(`events.${event.key}.push_enabled`, v)}
                        />
                        <span className="text-xs text-muted-foreground">Push</span>
                      </div>
                      <div className="flex items-center gap-2 pl-4 border-l">
                        <Switch 
                          checked={settings?.events?.[event.key]?.enabled ?? true}
                          onCheckedChange={(v) => updateSetting(`events.${event.key}.enabled`, v)}
                        />
                        <span className="text-xs font-medium">Attivo</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB TEMPLATE */}
        <TabsContent value="template" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Template Email
              </CardTitle>
              <CardDescription>
                Personalizza l'aspetto delle email inviate dalla piattaforma
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Color Source Selection */}
              <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                <h4 className="font-medium">Colore Primario Email</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div 
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      settings?.email_template?.color_source === 'admin' 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-muted-foreground'
                    }`}
                    onClick={() => updateSetting('email_template.color_source', 'admin')}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div 
                        className="w-6 h-6 rounded-full border-2" 
                        style={{ backgroundColor: settings?.email_template?.primary_color || '#16a34a' }}
                      />
                      <span className="font-medium">Colore Admin</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Usa il colore configurato qui sotto per tutte le email
                    </p>
                  </div>
                  <div 
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      settings?.email_template?.color_source === 'client' 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-muted-foreground'
                    }`}
                    onClick={() => updateSetting('email_template.color_source', 'client')}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-6 h-6 rounded-full border-2 bg-linear-to-r from-blue-500 via-green-500 to-purple-500" />
                      <span className="font-medium">Colore Cliente</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Usa il colore brand dell'azienda destinataria (se disponibile)
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Colore Primario {settings?.email_template?.color_source === 'client' && '(fallback)'}</Label>
                  <div className="flex gap-2">
                    <Input 
                      type="color"
                      value={settings?.email_template?.primary_color || '#16a34a'}
                      onChange={(e) => updateSetting('email_template.primary_color', e.target.value)}
                      className="w-12 h-10 p-1"
                    />
                    <Input 
                      value={settings?.email_template?.primary_color || '#16a34a'}
                      onChange={(e) => updateSetting('email_template.primary_color', e.target.value)}
                    />
                  </div>
                  {settings?.email_template?.color_source === 'client' && (
                    <p className="text-xs text-muted-foreground">
                      Usato se l'azienda destinataria non ha un colore brand configurato
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>URL Logo</Label>
                  <Input 
                    value={settings?.email_template?.logo_url || ''}
                    onChange={(e) => updateSetting('email_template.logo_url', e.target.value)}
                    placeholder="https://example.com/logo.png"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Header HTML (opzionale)</Label>
                <Textarea 
                  value={settings?.email_template?.header_html || ''}
                  onChange={(e) => updateSetting('email_template.header_html', e.target.value)}
                  placeholder="<div>Header personalizzato...</div>"
                  rows={4}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Footer HTML (opzionale)</Label>
                <Textarea 
                  value={settings?.email_template?.footer_html || ''}
                  onChange={(e) => updateSetting('email_template.footer_html', e.target.value)}
                  placeholder="<div>Footer personalizzato...</div>"
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminNotificationsPage;
